import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { rrApi, CAUSE_ORDER, CAUSE_LABELS } from "../../lib/rejectRemakeApi";
import { fetchProductionStages, stageLabel } from "../../lib/productionStages";

/**
 * Modal untuk membuat laporan reject/remake baru.
 * Trigger dari parent via `open` + `onOpenChange` props.
 */
export default function ReportModal({ open, onOpenChange, woId, stages = [], onCreated }) {
  const [type, setType] = useState("REJECT");
  const [stageCode, setStageCode] = useState("");
  const [qtyAffected, setQtyAffected] = useState(1);
  const [qtyTargetType, setQtyTargetType] = useState("ATASAN");
  const [causeCode, setCauseCode] = useState("");
  // Master HANYA dipakai untuk NAMA tampilan (name memang tidak disalin ke
  // baris WO, jadi nama terkini yang benar). Daftar PILIHAN tidak dari sini.
  const [stageMaster, setStageMaster] = useState([]);
  useEffect(() => {
    fetchProductionStages().then(setStageMaster).catch((e) => { setStageMaster([]); toast.error(apiErrorMessage(e, "Daftar tahap produksi gagal dimuat — muat ulang halaman.")); });
  }, []);

  // Pilihan tahap = tahap MILIK WO INI, bukan daftar master.
  //
  // Sebelum tahap menjadi data master, membedakan keduanya tidak ada gunanya
  // — tahap adalah konstanta, jadi master selalu sama dengan tahap tiap WO.
  // Sejak master bisa disunting, perbedaan itu mungkin: WO yang dikunci
  // sebelum sebuah tahap ditambahkan tidak pernah melewatinya. Menawarkan
  // tahap itu berarti mengizinkan reject tercatat pada sesuatu yang tidak
  // terjadi — merusak HPP per tahap dan Pareto penyebab reject.
  //
  // Backend menolaknya juga (STAGE_NOT_IN_WO); ini supaya pengguna tidak
  // pernah sampai ke penolakan itu.
  const stageOptions = [...(stages || [])]
    .sort((a, b) => a.stage_order - b.stage_order)
    .map((s) => ({
      code: s.stage_code,
      label: stageLabel(stageMaster, s.stage_code),
      order: s.stage_order,
    }));
  const [causeNotes, setCauseNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => {
    setType("REJECT"); setStageCode(""); setQtyAffected(1);
    setQtyTargetType("ATASAN"); setCauseCode(""); setCauseNotes("");
    setBusy(false); setErr(null);
  };

  const otherRequiresNotes = causeCode === "OTHER" && causeNotes.trim().length < 5;
  const canSubmit =
    !!stageCode && !!causeCode && qtyAffected >= 1 && !otherRequiresNotes && !busy;

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      // Kalau stages berisi objek dengan matching stage_code, kirimkan stage_id.
      const stageObj = stages.find(s => s.stage_code === stageCode);
      const payload = {
        stage_id: stageObj?.id || null,
        stage_code: stageCode,
        type,
        qty_affected: Number(qtyAffected),
        qty_target_type: qtyTargetType,
        cause_code: causeCode,
        cause_notes: causeNotes.trim() || null,
      };
      await rrApi.create(woId, payload);
      toast.success("Laporan reject/remake berhasil dibuat.");
      onCreated?.();
      reset();
      onOpenChange(false);
    } catch (e) {
      setErr(apiErrorMessage(e, "Laporan gagal dikirim."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="rr-report-modal">
        <DialogHeader>
          <DialogTitle>Lapor Reject / Remake</DialogTitle>
          <DialogDescription>
            Laporkan pcs cacat yang perlu di-reject (dibuang) atau di-remake (diperbaiki).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type toggle */}
          <div>
            <label className="text-sm font-medium block mb-2">Tipe</label>
            <div className="flex gap-2">
              {["REJECT", "REMAKE"].map(t => (
                <Button
                  key={t}
                  type="button"
                  variant={type === t ? "default" : "outline"}
                  size="sm"
                  onClick={() => setType(t)}
                  data-testid={`rr-report-type-${t.toLowerCase()}`}
                  className={type === t
                    ? (t === "REJECT" ? "bg-danger text-danger-foreground hover:bg-danger/90" : "bg-warning text-warning-foreground hover:bg-warning/90")
                    : ""}
                >{t}</Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {type === "REJECT" ? "REJECT = pcs cacat harus dibuang." : "REMAKE = pcs cacat diperbaiki (jahit ulang)."}
            </p>
          </div>

          {/* Stage */}
          <div>
            <label className="text-sm font-medium block mb-2">Tahap</label>
            <Select value={stageCode} onValueChange={setStageCode}>
              <SelectTrigger data-testid="rr-report-stage"><SelectValue placeholder="Pilih tahap produksi" /></SelectTrigger>
              <SelectContent>
                {stageOptions.map(s => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.order}. {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Qty + Target */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-2">Jumlah (pcs)</label>
              <Input
                type="number" min={1} value={qtyAffected}
                onChange={(e) => setQtyAffected(e.target.value)}
                data-testid="rr-report-qty"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Bagian</label>
              <div className="flex gap-2">
                {["ATASAN", "CELANA"].map(t => (
                  <Button
                    key={t}
                    type="button"
                    variant={qtyTargetType === t ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQtyTargetType(t)}
                    data-testid={`rr-report-target-${t.toLowerCase()}`}
                  >{t === "ATASAN" ? "Atasan" : "Celana"}</Button>
                ))}
              </div>
            </div>
          </div>

          {/* Cause */}
          <div>
            <label className="text-sm font-medium block mb-2">Penyebab</label>
            <Select value={causeCode} onValueChange={setCauseCode}>
              <SelectTrigger data-testid="rr-report-cause"><SelectValue placeholder="Pilih penyebab" /></SelectTrigger>
              <SelectContent>
                {CAUSE_ORDER.map(c => (
                  <SelectItem key={c} value={c}>{CAUSE_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium block mb-2">
              Catatan Detail {causeCode === "OTHER" && <span className="text-danger">*</span>}
            </label>
            <Textarea
              rows={3} maxLength={500}
              value={causeNotes}
              placeholder={causeCode === "OTHER" ? "Wajib diisi min 5 karakter." : "Detail penyebab (opsional)…"}
              onChange={(e) => setCauseNotes(e.target.value)}
              data-testid="rr-report-notes"
            />
            {otherRequiresNotes && (
              <p className="text-xs text-danger mt-1">Catatan wajib diisi minimal 5 karakter untuk penyebab &quot;Lain-lain&quot;.</p>
            )}
          </div>

          {err && (
            <div className="flex items-start gap-2 text-xs text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded px-3 py-2" data-testid="rr-report-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span className="break-all">{err}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Batal</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="rr-report-submit">
            {busy ? "Mengirim…" : "Kirim Laporan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Q68-01 Batch 7. `TypeBadge` DIHAPUS dari sini. Docstring-nya mengaku
// "reused across list card + detail", tapi grep atas seluruh `frontend/src`
// mengembalikan NOL pengimpor — ia sudah lama mati sambil menyimpan salinan
// keempat warna REJECT/REMAKE (latar merah-100 dan kuning-100 literal). Penggantinya
// yang hidup adalah `RRTypeBadge` di `components/etams/StatusBadge.jsx`.
